import { WEATHER_CACHE_STORE, openDeviceDatabase } from "./device-storage";
import { routeCoordinates, type RouteCoordinate } from "./navigation-core";
import type { RouteGeoJson } from "./route-workflow";
import { Capacitor } from "@capacitor/core";
export const INAMHI_ALERTS_URL = "https://servicios.inamhi.gob.ec/alertas/";
export const OPEN_METEO_URL = "https://open-meteo.com/";
const WEATHER_API_BASE =
  Capacitor.getPlatform() === "android"
    ? "https://thera-rutas-ecuador.pages.dev"
    : "";
export type WeatherLocationName = "Ubicación" | "Inicio" | "Punto más alto" | "Final";

export type RouteWeatherPoint = {
  name: WeatherLocationName;
  latitude: number;
  longitude: number;
  elevationM: number | null;
  time: string;
  temperatureC: number | null;
  precipitationMm: number;
  rainMm: number;
  precipitationProbability: number | null;
  windKmh: number | null;
  gustKmh: number | null;
  visibilityM: number | null;
  uvIndex: number | null;
  weatherCode: number | null;
  next6HoursPrecipitationMm: number;
};

export type RouteWeather = {
  routeId: string;
  updatedAt: number;
  offline: boolean;
  points: RouteWeatherPoint[];
  advisories: string[];
};

type OpenMeteoLocation = {
  latitude: number;
  longitude: number;
  elevation?: number;
  hourly?: {
    time?: string[];
    temperature_2m?: Array<number | null>;
    precipitation?: Array<number | null>;
    rain?: Array<number | null>;
    precipitation_probability?: Array<number | null>;
    weather_code?: Array<number | null>;
    wind_speed_10m?: Array<number | null>;
    wind_gusts_10m?: Array<number | null>;
    visibility?: Array<number | null>;
    uv_index?: Array<number | null>;
  };
};

function selectWeatherCoordinates(geojson: RouteGeoJson) {
  const coordinates = routeCoordinates(geojson);
  if (coordinates.length < 2) return [];
  const highest = coordinates.reduce((best, point) => {
    if (point.elevation === null) return best;
    return best.elevation === null || point.elevation > best.elevation ? point : best;
  }, coordinates[Math.floor(coordinates.length / 2)]);
  return [coordinates[0], highest, coordinates.at(-1)!];
}

function nearestHour(times: string[]) {
  const now = Date.now();
  return times.reduce((best, time, index) => {
    const ecuadorTime = /(?:Z|[+-]\d{2}:?\d{2})$/.test(time) ? time : `${time}:00-05:00`;
    const difference = Math.abs(new Date(ecuadorTime).getTime() - now);
    return difference < best.difference ? { index, difference } : best;
  }, { index: 0, difference: Number.POSITIVE_INFINITY }).index;
}

function value(values: Array<number | null> | undefined, index: number) {
  const current = values?.[index];
  return typeof current === "number" && Number.isFinite(current) ? current : null;
}

function preventiveAdvisories(points: RouteWeatherPoint[]) {
  const messages = new Set<string>();
  points.forEach((point) => {
    if ((point.gustKmh ?? 0) >= 60) messages.add("Ráfagas fuertes: evita crestas expuestas y revisa la estabilidad del grupo.");
    else if ((point.windKmh ?? 0) >= 40) messages.add("Viento fuerte previsto en sectores expuestos.");
    if (point.next6HoursPrecipitationMm >= 8) messages.add("Lluvia importante prevista: considera terreno resbaladizo y crecidas.");
    if ((point.visibilityM ?? Number.POSITIVE_INFINITY) < 1000) messages.add("Visibilidad reducida: conserva el GPX y los mapas offline preparados.");
    if ((point.uvIndex ?? 0) >= 8) messages.add("Radiación UV muy alta: usa protección solar, gafas y cobertura.");
    if ((point.temperatureC ?? 99) <= 3) messages.add("Temperatura baja: lleva capas térmicas e impermeables.");
  });
  return Array.from(messages);
}

function normalise(routeId: string, payload: OpenMeteoLocation | OpenMeteoLocation[], names: WeatherLocationName[]): RouteWeather {
  const locations = Array.isArray(payload) ? payload : [payload];
  const points = locations.slice(0, 3).map((location, locationIndex) => {
    const hourly = location.hourly ?? {};
    const times = hourly.time ?? [];
    const index = nearestHour(times);
    const futurePrecipitation = (hourly.precipitation ?? []).slice(index, index + 6)
      .reduce<number>((total, amount) => total + (typeof amount === "number" ? amount : 0), 0);
    return {
      name: names[locationIndex],
      latitude: location.latitude,
      longitude: location.longitude,
      elevationM: typeof location.elevation === "number" ? Math.round(location.elevation) : null,
      time: times[index] ?? new Date().toISOString(),
      temperatureC: value(hourly.temperature_2m, index),
      precipitationMm: value(hourly.precipitation, index) ?? 0,
      rainMm: value(hourly.rain, index) ?? 0,
      precipitationProbability: value(hourly.precipitation_probability, index),
      windKmh: value(hourly.wind_speed_10m, index),
      gustKmh: value(hourly.wind_gusts_10m, index),
      visibilityM: value(hourly.visibility, index),
      uvIndex: value(hourly.uv_index, index),
      weatherCode: value(hourly.weather_code, index),
      next6HoursPrecipitationMm: Math.round(futurePrecipitation * 10) / 10,
    } satisfies RouteWeatherPoint;
  });
  return { routeId, updatedAt: Date.now(), offline: false, points, advisories: preventiveAdvisories(points) };
}

async function saveWeather(value: RouteWeather) {
  if (!("indexedDB" in window)) return;
  const database = await openDeviceDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(WEATHER_CACHE_STORE, "readwrite");
    transaction.objectStore(WEATHER_CACHE_STORE).put(value);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
  database.close();
}

export async function loadStoredWeather(routeId: string) {
  if (!("indexedDB" in window)) return null;
  const database = await openDeviceDatabase();
  const result = await new Promise<RouteWeather | null>((resolve, reject) => {
    const request = database.transaction(WEATHER_CACHE_STORE, "readonly").objectStore(WEATHER_CACHE_STORE).get(routeId);
    request.onsuccess = () => resolve(request.result ?? null);
    request.onerror = () => reject(request.error);
  });
  database.close();
  return result ? { ...result, offline: true } : null;
}

async function fetchWeather(
  routeId: string,
  coordinates: RouteCoordinate[],
  names: WeatherLocationName[],
) {
  if (!coordinates.length || coordinates.length > 3) throw new Error("No existe una ubicación válida para consultar el pronóstico.");
  const points = coordinates.map((point) => [point.latitude, point.longitude, point.elevation ?? ""].join(",")).join("|");
  try {
    const response = await fetch(`${WEATHER_API_BASE}/api/weather/route?points=${encodeURIComponent(points)}`, { headers: { Accept: "application/json" } });
    if (!response.ok) throw new Error("Pronóstico no disponible.");
    const weather = normalise(routeId, await response.json() as OpenMeteoLocation | OpenMeteoLocation[], names);
    await saveWeather(weather).catch(() => undefined);
    return weather;
  } catch (error) {
    const stored = await loadStoredWeather(routeId).catch(() => null);
    if (stored) return stored;
    throw error;
  }
}
export function weatherConditionLabel(
  point: Pick<RouteWeatherPoint, "weatherCode" | "precipitationMm" | "rainMm">,
) {
  const code = point.weatherCode;

  if ((point.rainMm ?? 0) > 0.1 || (point.precipitationMm ?? 0) > 0.1) {
    if (code !== null && code >= 95) return "tormenta";
    return "lluvia";
  }

  if (code === 0) return "despejado";
  if (code !== null && code >= 1 && code <= 3) return "parcialmente nublado";
  if (code === 45 || code === 48) return "niebla";
  if (code !== null && code >= 51 && code <= 57) return "llovizna";
  if (code !== null && code >= 61 && code <= 67) return "lluvia";
  if (code !== null && code >= 71 && code <= 77) return "nieve";
  if (code !== null && code >= 80 && code <= 82) return "chubascos";
  if (code !== null && code >= 85 && code <= 86) return "nieve";
  if (code !== null && code >= 95) return "tormenta";

  return "condición meteorológica disponible";
}
export async function fetchRouteWeather(routeId: string, geojson: RouteGeoJson) {
  const coordinates = selectWeatherCoordinates(geojson);
  if (coordinates.length !== 3) throw new Error("La ruta necesita un trazado para consultar el pronóstico.");
  return fetchWeather(routeId, coordinates, ["Inicio", "Punto más alto", "Final"]);
}

export async function fetchPointWeather(routeId: string, point: RouteCoordinate) {
  return fetchWeather(routeId, [point], ["Ubicación"]);
}
